import React, { useState } from "react";
import { Download, Copy, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const ASSETS = [
  {
    section: "Logo",
    items: [
      { name: "Primary logo — gold on ink (PNG)", file: "/brand-kit/logo/jj-argent-logo.png", dim: "1600×512", note: "Full mark + wordmark on dark. Use on dark backgrounds — LinkedIn banners, dark slides, video overlays." },
      { name: "Primary logo — gold on ink (SVG)", file: "/brand-kit/logo/jj-argent-logo.svg", dim: "SVG", note: "Scalable vector — use in print, decks and anywhere sharpness matters at any size." },
      { name: "White wordmark — on ink (PNG)", file: "/brand-kit/logo/jj-argent-logo-white.png", dim: "1600×512", note: "Alt lockup: white JJ ARGENT with gold mark. Matches the site nav treatment." },
      { name: "White wordmark — on ink (SVG)", file: "/brand-kit/logo/jj-argent-logo-white.svg", dim: "SVG" },
      { name: "Light lockup — on cream (PNG)", file: "/brand-kit/logo/jj-argent-logo-light.png", dim: "1600×512", note: "Use on light / cream / white backgrounds — letterheads, print." },
      { name: "Light lockup — on cream (SVG)", file: "/brand-kit/logo/jj-argent-logo-light.svg", dim: "SVG" },
      { name: "Transparent lockup (PNG)", file: "/brand-kit/logo/jj-argent-logo-transparent.png", dim: "1600×512", note: "No background — drop onto photography, video, custom backgrounds." },
      { name: "Transparent lockup (SVG)", file: "/brand-kit/logo/jj-argent-logo-transparent.svg", dim: "SVG" },
      { name: "Mark only (SVG)", file: "/brand-kit/logo/jj-argent-mark.svg", dim: "SVG", note: "Just the folding-table-to-dots mark, no wordmark. Use as a monogram, avatar, favicon source." },
      { name: "Mark only 1024 (PNG)", file: "/brand-kit/logo/jj-argent-mark-1024.png", dim: "1024×1024" },
      { name: "Mark only 256 (PNG)", file: "/brand-kit/logo/jj-argent-mark-256.png", dim: "256×256" },
    ],
  },
  {
    section: "Social & sharing",
    items: [
      { name: "OG social share card", file: "/og-image.jpg", dim: "1200×630", note: "Auto-used by LinkedIn, Twitter, Slack, WhatsApp when jjargent.com is shared" },
      { name: "Quote card", file: "/brand-kit/quote-card.jpg", dim: "1200×630", note: "Tagline-only; use for feed posts, slide decks, reshares" },
      { name: "LinkedIn banner", file: "/brand-kit/linkedin-banner.jpg", dim: "1584×396", note: "Paste as your LinkedIn profile background" },
    ],
  },
  {
    section: "Case study feed cards",
    items: [
      { name: "Carpark → 8,000+ weekly students", file: "/brand-kit/feed-card-carpark-to-8000.jpg", dim: "1080×1080" },
      { name: "Orientation Week at scale", file: "/brand-kit/feed-card-orientation-scale.jpg", dim: "1080×1080" },
      { name: "Calgary Pride 100,000+", file: "/brand-kit/feed-card-calgary-pride.jpg", dim: "1080×1080" },
      { name: "Rebuilding U of Toronto services", file: "/brand-kit/feed-card-toronto-eventpro.jpg", dim: "1080×1080" },
      { name: "Monash Open Day", file: "/brand-kit/feed-card-monash-openday.jpg", dim: "1080×1080" },
      { name: "Spreadsheets to systems", file: "/brand-kit/feed-card-spreadsheet-to-systems.jpg", dim: "1080×1080" },
    ],
  },
  {
    section: "CV & résumé",
    items: [
      { name: "CV header strip", file: "/brand-kit/cv-header.jpg", dim: "2100×450", note: "Paste at the top of your PDF résumé" },
      { name: "Executive CV (PDF)", file: "/brand-kit/JJ_Argent_Executive_CV.pdf", dim: "PDF", note: "Canonical Executive CV — single source of truth" },
    ],
  },
  {
    section: "Email signature",
    items: [
      { name: "HTML signature (Gmail / Outlook / Apple Mail)", file: "/brand-kit/email-signature.html", dim: "HTML", note: "Copy the HTML block below, then paste into your mail client's signature settings" },
      { name: "PNG signature fallback", file: "/brand-kit/email-signature.png", dim: "800×260", note: "Use if HTML rich-text pasting isn't supported" },
      { name: "Signature portrait", file: "/brand-kit/signature-portrait.jpg", dim: "240×240" },
    ],
  },
  {
    section: "Favicon set",
    items: [
      { name: "Favicon 512", file: "/brand-kit/favicon.png", dim: "512×512" },
      { name: "Favicon 192", file: "/favicon-192.png", dim: "192×192" },
      { name: "Apple touch icon", file: "/apple-touch-icon.png", dim: "180×180" },
    ],
  },
];

const SIGNATURE_HTML_URL = "/brand-kit/email-signature.html";

const filenameFromPath = (p) => {
  try { return decodeURIComponent(p.split("/").pop() || "download"); }
  catch { return "download"; }
};

async function forceDownload(url, filename) {
  // Native <a download> only works reliably for same-origin URLs; force the
  // download by fetching → blob → creating a temporary object-URL anchor.
  try {
    const r = await fetch(url, { credentials: "omit" });
    if (!r.ok) throw new Error(`Fetch failed: ${r.status}`);
    const blob = await r.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename || filenameFromPath(url);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
  } catch (e) {
    // Fallback: open in new tab so user can right-click → save
    window.open(url, "_blank", "noopener");
  }
}

export default function BrandKit() {
  useDocumentMeta({
    title: "Brand Kit — JJ Argent",
    description:
      "Downloadable brand assets for JJ Argent — social share cards, LinkedIn banner, feed cards, CV header, favicon set and HTML email signature.",
    path: "/brand-kit",
  });
  const [copied, setCopied] = useState(false);
  const [snippet, setSnippet] = useState("");

  React.useEffect(() => {
    fetch(SIGNATURE_HTML_URL)
      .then((r) => r.text())
      .then(setSnippet)
      .catch(() => setSnippet(""));
  }, []);

  const copySignature = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success("Signature HTML copied");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copy failed — select the box and use ⌘/Ctrl+C");
    }
  };

  return (
    <main id="main" data-testid="brand-kit-page" className="bg-[#0B0B0C] text-[#FAF9F6] min-h-screen">
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-16">
        <p className="eyebrow" data-testid="brand-kit-eyebrow">Brand kit</p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
          Everything the site is made of<span className="text-[#D8A936]">.</span>
        </h1>
        <p className="mt-6 text-[#A9A9A6] max-w-2xl text-base leading-relaxed">
          Downloadable assets in the same visual system as this site — for LinkedIn, résumés, decks, email, or anywhere the JJ Argent brand needs to look consistent. Everything below is free to use for anything associated with JJ.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-16">
        <div className="border border-[#343437] bg-[#151517]/60 p-6 lg:p-8" data-testid="brand-kit-signature-block">
          <p className="eyebrow">Email signature · HTML</p>
          <h2 className="mt-3 font-display text-2xl lg:text-3xl">Paste into Gmail, Outlook or Apple Mail</h2>
          <p className="mt-3 text-sm text-[#A9A9A6] leading-relaxed max-w-2xl">
            In Gmail → Settings → General → Signature, click <em>Insert as HTML</em> (or paste as rich text). The portrait is served from <code className="text-[#D8A936]">jjargent.com</code> so email clients render it inline.
          </p>
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={copySignature}
              data-testid="brand-kit-copy-signature"
              className="btn-primary"
            >
              {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy signature HTML"}
            </button>
            <button
              type="button"
              onClick={() => forceDownload(SIGNATURE_HTML_URL, "email-signature.html")}
              data-testid="brand-kit-download-signature"
              className="btn-ghost-dark"
            >
              <Download size={14} />
              Download .html
            </button>
            <button
              type="button"
              onClick={() => forceDownload("/brand-kit/email-signature.png", "email-signature.png")}
              className="btn-ghost-dark"
            >
              <Download size={14} />
              Download .png fallback
            </button>
          </div>
          <div className="mt-6 border border-[#343437] bg-[#0B0B0C] p-4 max-h-64 overflow-y-auto">
            <pre className="text-[11px] text-[#A9A9A6] font-mono whitespace-pre-wrap leading-relaxed">
              {snippet || "Loading signature HTML…"}
            </pre>
          </div>
          <div className="mt-6 border border-[#343437] bg-[#0B0B0C] p-6 flex justify-center">
            <img
              src="/brand-kit/email-signature.png"
              alt="JJ Argent email signature preview"
              className="max-w-full h-auto"
              data-testid="brand-kit-signature-preview"
            />
          </div>
        </div>
      </section>

      {ASSETS.map((group) => (
        <section
          key={group.section}
          data-testid={`brand-kit-group-${group.section.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className="max-w-6xl mx-auto px-6 lg:px-10 pb-16"
        >
          <p className="eyebrow">{group.section}</p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {group.items.map((asset) => (
              <div
                key={asset.file}
                className="group border border-[#343437] bg-[#151517]/60 p-5 hover:border-[#D8A936] transition-colors flex flex-col"
              >
                <div className="aspect-[16/9] bg-[#0B0B0C] border border-[#343437] flex items-center justify-center overflow-hidden mb-4">
                  {asset.file.endsWith(".pdf") || asset.file.endsWith(".html") ? (
                    <div className="text-center">
                      <ExternalLink size={28} className="mx-auto text-[#D8A936] mb-2" />
                      <p className="text-[10px] tracking-[0.14em] uppercase text-[#6E6E6B]">{asset.dim}</p>
                    </div>
                  ) : (
                    <img
                      src={asset.file}
                      alt={asset.name}
                      loading="lazy"
                      className="max-w-full max-h-full object-contain group-hover:scale-[1.02] transition-transform"
                    />
                  )}
                </div>
                <div className="flex items-start justify-between gap-3 flex-1">
                  <div>
                    <p className="text-sm text-[#FAF9F6] group-hover:text-[#D8A936] transition-colors">
                      {asset.name}
                    </p>
                    <p className="text-[10px] tracking-[0.14em] uppercase text-[#6E6E6B] mt-1">{asset.dim}</p>
                    {asset.note && (
                      <p className="text-xs text-[#A9A9A6] mt-2 leading-relaxed">{asset.note}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => forceDownload(asset.file, filenameFromPath(asset.file))}
                    data-testid={`brand-kit-download-${asset.file.replace(/[^a-z0-9]+/gi, "-")}`}
                    className="shrink-0 mt-1 border border-[#343437] px-2.5 py-1.5 text-[10px] tracking-[0.14em] uppercase text-[#A9A9A6] hover:border-[#D8A936] hover:text-[#D8A936] transition-colors flex items-center gap-1.5"
                  >
                    <Download size={12} />
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-24">
        <div className="border border-[#D8A936]/40 bg-[#D8A936]/5 p-6 lg:p-8">
          <p className="eyebrow" style={{ color: "#D8A936" }}>Colour system</p>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { hex: "#0B0B0C", label: "Ink" },
              { hex: "#151517", label: "Card" },
              { hex: "#D8A936", label: "Gold" },
              { hex: "#FAF9F6", label: "Paper" },
              { hex: "#A9A9A6", label: "Muted" },
            ].map((c) => (
              <div key={c.hex} className="border border-[#343437] p-3">
                <div className="aspect-square" style={{ background: c.hex }} />
                <p className="text-xs text-[#FAF9F6] mt-2 font-mono">{c.hex}</p>
                <p className="text-[10px] tracking-[0.14em] uppercase text-[#6E6E6B]">{c.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
